import { FRAMES_DISCOUNTS_RULE_TYPE } from "./shared";
import { rateGuarantee } from "./sharedTypedAmount";

import type { TypedAmountTypeOption } from "../typedAmountTypes";

const NOT_INCLUDED: TypedAmountTypeOption = {
  value: "notIncluded",
  label: "Not Included",
  hideAmount: true,
  hideDependents: true,
};
const NOT_DISCLOSED: TypedAmountTypeOption = {
  value: "notDisclosed",
  label: "Not Disclosed",
  hideAmount: true,
  hideDependents: true,
};
const NOT_OFFERED: TypedAmountTypeOption = {
  value: "notOffered",
  label: "Not Offered",
  hideAmount: true,
  hideDependents: true,
};

const COPAY = "Copay Amount";
const ALLOWANCE = "Allowance Amount";

const COPAY_OPTIONS: TypedAmountTypeOption[] = [
  { value: "dollarAmount", label: "Dollar amount" },
  NOT_INCLUDED,
  NOT_DISCLOSED,
  NOT_OFFERED,
];

const COVERED_IN_FULL_AFTER_COPAY = "coveredInFullAfterCopay";
const DOLLAR_AMOUNT_ALLOWANCE = "dollarAmountAllowance";
const DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY =
  "dollarAmountAllowanceWithoutCopay";
const DOLLAR_AMOUNT_ALLOWANCE_WITH_COPAY = "dollarAmountAllowanceWithCopay";
const DOLLAR_AMOUNT_ALLOWANCE_LABEL = "Dollar Amount Allowance";
const DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY_LABEL =
  "Dollar Amount Allowance without Copay";

const COVERED_IN_FULL_AFTER_COPAY_OPTION: TypedAmountTypeOption = {
  value: COVERED_IN_FULL_AFTER_COPAY,
  label: "Covered in Full After Copay",
};
const DOLLAR_AMOUNT_ALLOWANCE_OPTION: TypedAmountTypeOption = {
  value: DOLLAR_AMOUNT_ALLOWANCE,
  label: DOLLAR_AMOUNT_ALLOWANCE_LABEL,
  hideAmount: true,
};
const DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY_OPTION: TypedAmountTypeOption = {
  value: DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY,
  label: DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY_LABEL,
  hideAmount: true,
};
const DOLLAR_AMOUNT_ALLOWANCE_WITH_COPAY_OPTION: TypedAmountTypeOption = {
  value: DOLLAR_AMOUNT_ALLOWANCE_WITH_COPAY,
  label: "Dollar Amount Allowance with Copay",
};

// RfpVisionCopayFullAllowanceRuleType — Single/Bifocal/Trifocal/Lenticular Lenses.
// `hideAmount` marks types where the primary (Copay) amount is hidden; the
// Allowance extra is separately gated by its own `showForTypes` list below.
// Mirrors the Build tab's disabledValues rules in vision/definitions.ts.
const COPAY_FULL_ALLOWANCE_OPTIONS: TypedAmountTypeOption[] = [
  COVERED_IN_FULL_AFTER_COPAY_OPTION,
  { value: "coveredInFull", label: "Covered in Full", hideAmount: true },
  DOLLAR_AMOUNT_ALLOWANCE_OPTION,
  DOLLAR_AMOUNT_ALLOWANCE_WITH_COPAY_OPTION,
  NOT_INCLUDED,
  NOT_DISCLOSED,
  NOT_OFFERED,
];
const COPAY_FULL_ALLOWANCE_SHOWS_ALLOWANCE = [
  DOLLAR_AMOUNT_ALLOWANCE,
  DOLLAR_AMOUNT_ALLOWANCE_WITH_COPAY,
];

// RfpVisionCopayAllowanceRuleType — Standard Progressive, Contact Lens Exam.
const COPAY_ALLOWANCE_OPTIONS: TypedAmountTypeOption[] = [
  COVERED_IN_FULL_AFTER_COPAY_OPTION,
  DOLLAR_AMOUNT_ALLOWANCE_OPTION,
  DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY_OPTION,
  DOLLAR_AMOUNT_ALLOWANCE_WITH_COPAY_OPTION,
  NOT_INCLUDED,
  NOT_DISCLOSED,
  NOT_OFFERED,
];
const COPAY_ALLOWANCE_SHOWS_ALLOWANCE = [
  COVERED_IN_FULL_AFTER_COPAY,
  DOLLAR_AMOUNT_ALLOWANCE,
  DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY,
  DOLLAR_AMOUNT_ALLOWANCE_WITH_COPAY,
];

// RfpVisionSpecialCopayAllowanceRuleType — Standard Frames, Contact Lens Elective.
const SPECIAL_COPAY_ALLOWANCE_OPTIONS: TypedAmountTypeOption[] = [
  DOLLAR_AMOUNT_ALLOWANCE_OPTION,
  DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY_OPTION,
  DOLLAR_AMOUNT_ALLOWANCE_WITH_COPAY_OPTION,
  NOT_INCLUDED,
  NOT_DISCLOSED,
  NOT_OFFERED,
];
const SPECIAL_COPAY_ALLOWANCE_SHOWS_ALLOWANCE = [
  DOLLAR_AMOUNT_ALLOWANCE,
  DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY,
  DOLLAR_AMOUNT_ALLOWANCE_WITH_COPAY,
];

const PREMIUM_PROGRESSIVE_OPTIONS: TypedAmountTypeOption[] = [
  COVERED_IN_FULL_AFTER_COPAY_OPTION,
  DOLLAR_AMOUNT_ALLOWANCE_OPTION,
  DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY_OPTION,
  DOLLAR_AMOUNT_ALLOWANCE_WITH_COPAY_OPTION,
  NOT_INCLUDED,
  NOT_DISCLOSED,
  NOT_OFFERED,
];
const PREMIUM_PROGRESSIVE_SHOWS_ALLOWANCE = [
  DOLLAR_AMOUNT_ALLOWANCE,
  DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY,
  DOLLAR_AMOUNT_ALLOWANCE_WITH_COPAY,
];

const PROGRESSIVE_LENSES_OPTIONS: TypedAmountTypeOption[] = [
  COVERED_IN_FULL_AFTER_COPAY_OPTION,
  DOLLAR_AMOUNT_ALLOWANCE_OPTION,
  DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY_OPTION,
  NOT_INCLUDED,
  NOT_DISCLOSED,
  NOT_OFFERED,
];
const PROGRESSIVE_LENSES_SHOWS_ALLOWANCE = [
  DOLLAR_AMOUNT_ALLOWANCE,
  DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY,
];

const CONTACT_LENS_MED_NEC_OPTIONS: TypedAmountTypeOption[] = [
  COVERED_IN_FULL_AFTER_COPAY_OPTION,
  { value: "coveredInFull", label: "Covered in Full", hideAmount: true },
  DOLLAR_AMOUNT_ALLOWANCE_OPTION,
  NOT_INCLUDED,
  NOT_DISCLOSED,
  NOT_OFFERED,
];
const CONTACT_LENS_MED_NEC_SHOWS_ALLOWANCE = [DOLLAR_AMOUNT_ALLOWANCE];

const DOLLAR_AMOUNT_ALLOWANCE_WITH_ADDITIONAL_DISCOUNT =
  "dollarAmountAllowanceWithAdditionalDiscount";
const CONVENTIONAL_CONTACT_OPTIONS: TypedAmountTypeOption[] = [
  { value: DOLLAR_AMOUNT_ALLOWANCE, label: DOLLAR_AMOUNT_ALLOWANCE_LABEL },
  {
    value: DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY,
    label: DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY_LABEL,
  },
  {
    value: DOLLAR_AMOUNT_ALLOWANCE_WITH_ADDITIONAL_DISCOUNT,
    label: "Dollar Amount Allowance with Additional Discount",
  },
  NOT_INCLUDED,
  NOT_DISCLOSED,
  NOT_OFFERED,
];

const ALLOWANCE_IN_LIEU_OF_GLASSES = "allowanceInLieuOfGlasses";
const ALLOWANCE_IN_LIEU_OF_GLASSES_WITH_COPAY =
  "allowanceInLieuOfGlassesWithCopay";
const DISPOSABLE_CONTACT_OPTIONS: TypedAmountTypeOption[] = [
  { value: DOLLAR_AMOUNT_ALLOWANCE, label: DOLLAR_AMOUNT_ALLOWANCE_LABEL },
  {
    value: DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY,
    label: DOLLAR_AMOUNT_ALLOWANCE_WITHOUT_COPAY_LABEL,
  },
  {
    value: ALLOWANCE_IN_LIEU_OF_GLASSES,
    label: "Allowance in Lieu of Glasses",
  },
  {
    value: ALLOWANCE_IN_LIEU_OF_GLASSES_WITH_COPAY,
    label: "Allowance in Lieu of Glasses with Copay",
  },
  NOT_INCLUDED,
  NOT_DISCLOSED,
  NOT_OFFERED,
];

const ADDITIONAL_DISCOUNTS_ON_FRAMES_TYPE_OPTIONS: TypedAmountTypeOption[] = [
  {
    value: FRAMES_DISCOUNTS_RULE_TYPE.dollar,
    label: "Up to Dollar Amount Off",
  },
  {
    value: FRAMES_DISCOUNTS_RULE_TYPE.percentage,
    label: "Up to Percentage Off",
  },
  NOT_INCLUDED,
  NOT_DISCLOSED,
  NOT_OFFERED,
];

export interface VisionTypedAmountSpec {
  typeKey: string;
  amountKey: string;
  amountLabel: string;
  typeOptions: TypedAmountTypeOption[];
  extraKeys?: {
    key: string;
    label: string;
    showForTypes?: string[];
  }[];
  secondary?: {
    label: string;
    typeKey: string;
    amountKey: string;
    amountLabel: string;
    typeOptions: TypedAmountTypeOption[];
    hideForTypes?: string[];
  };
  amountMin?: number;
  amountInteger?: boolean;
}

/** Builds a copay+allowance multi-amount field spec for the given prefix/options. */
const copayAllowanceField = (
  prefix: string,
  typeOptions: TypedAmountTypeOption[],
  showsAllowanceForTypes: string[],
): VisionTypedAmountSpec => ({
  typeKey: `${prefix}Type`,
  amountKey: `${prefix}CopayAmount`,
  amountLabel: COPAY,
  typeOptions,
  extraKeys: [
    {
      key: `${prefix}AllowanceAmount`,
      label: ALLOWANCE,
      showForTypes: showsAllowanceForTypes,
    },
  ],
});

export const visionTypedAmountFields: Record<string, VisionTypedAmountSpec> = {
  examCopay: {
    typeKey: "examCopayType",
    amountKey: "examCopayAmount",
    amountLabel: "Amount",
    typeOptions: COPAY_OPTIONS,
  },
  materialsCopay: {
    typeKey: "materialsCopayType",
    amountKey: "materialsCopayAmount",
    amountLabel: "Amount",
    typeOptions: COPAY_OPTIONS,
  },
  singleLenses: copayAllowanceField(
    "singleLenses",
    COPAY_FULL_ALLOWANCE_OPTIONS,
    COPAY_FULL_ALLOWANCE_SHOWS_ALLOWANCE,
  ),
  bifocalLenses: copayAllowanceField(
    "bifocalLenses",
    COPAY_FULL_ALLOWANCE_OPTIONS,
    COPAY_FULL_ALLOWANCE_SHOWS_ALLOWANCE,
  ),
  trifocalLenses: copayAllowanceField(
    "trifocalLenses",
    COPAY_FULL_ALLOWANCE_OPTIONS,
    COPAY_FULL_ALLOWANCE_SHOWS_ALLOWANCE,
  ),
  lenticularLenses: copayAllowanceField(
    "lenticularLenses",
    COPAY_FULL_ALLOWANCE_OPTIONS,
    COPAY_FULL_ALLOWANCE_SHOWS_ALLOWANCE,
  ),
  standardProgressive: copayAllowanceField(
    "standardProgressive",
    COPAY_ALLOWANCE_OPTIONS,
    COPAY_ALLOWANCE_SHOWS_ALLOWANCE,
  ),
  premiumProgressive: copayAllowanceField(
    "premiumProgressive",
    PREMIUM_PROGRESSIVE_OPTIONS,
    PREMIUM_PROGRESSIVE_SHOWS_ALLOWANCE,
  ),
  contactLensExam: copayAllowanceField(
    "contactLensExam",
    COPAY_ALLOWANCE_OPTIONS,
    COPAY_ALLOWANCE_SHOWS_ALLOWANCE,
  ),
  standardFrames: {
    ...copayAllowanceField(
      "standardFrames",
      SPECIAL_COPAY_ALLOWANCE_OPTIONS,
      SPECIAL_COPAY_ALLOWANCE_SHOWS_ALLOWANCE,
    ),
    secondary: {
      label: "Additional Discounts on Frames",
      typeKey: "additionalDiscountsOnFramesType",
      amountKey: "additionalDiscountsOnFramesAmount",
      amountLabel: "Amount",
      typeOptions: ADDITIONAL_DISCOUNTS_ON_FRAMES_TYPE_OPTIONS,
      hideForTypes: [
        NOT_INCLUDED.value,
        NOT_DISCLOSED.value,
        NOT_OFFERED.value,
      ],
    },
  },
  contactLensElective: copayAllowanceField(
    "contactLensElective",
    SPECIAL_COPAY_ALLOWANCE_OPTIONS,
    SPECIAL_COPAY_ALLOWANCE_SHOWS_ALLOWANCE,
  ),
  conventionalContact: {
    typeKey: "conventionalContactType",
    amountKey: "conventionalContactAllowanceAmount",
    amountLabel: ALLOWANCE,
    typeOptions: CONVENTIONAL_CONTACT_OPTIONS,
    extraKeys: [
      {
        key: "conventionalContactAdditionalDiscountAmount",
        label: "Additional Discount Amount",
        showForTypes: [DOLLAR_AMOUNT_ALLOWANCE_WITH_ADDITIONAL_DISCOUNT],
      },
    ],
  },
  progressiveLenses: copayAllowanceField(
    "progressiveLenses",
    PROGRESSIVE_LENSES_OPTIONS,
    PROGRESSIVE_LENSES_SHOWS_ALLOWANCE,
  ),
  contactLensMedNec: copayAllowanceField(
    "contactLensMedNec",
    CONTACT_LENS_MED_NEC_OPTIONS,
    CONTACT_LENS_MED_NEC_SHOWS_ALLOWANCE,
  ),
  disposableContact: {
    typeKey: "disposableContactType",
    amountKey: "disposableContactAllowanceAmount",
    amountLabel: ALLOWANCE,
    typeOptions: DISPOSABLE_CONTACT_OPTIONS,
    extraKeys: [
      {
        key: "disposableContactCopayAmount",
        label: COPAY,
        showForTypes: [ALLOWANCE_IN_LIEU_OF_GLASSES_WITH_COPAY],
      },
    ],
  },
  rateGuarantee,
};
